import {useMediaQuery} from '@mui/material'
import Lottie from 'lottie-react'
import moment from 'moment/moment'
import {FC, useCallback, useMemo} from 'react'
import {useDispatch} from 'react-redux'
import {useParams} from 'react-router'
import {useNavigate} from 'react-router-dom'
import {PhotoTypes, virtualGiftsOpenModal} from 'entities/messages'
import {CountChildrenObj, EducationMap, useProfile} from 'entities/profile'
import {useUser} from 'entities/user'
import {upgradeHandler} from 'features/payment/lib'
import {VirtualGiftsModal} from 'features/virtualGiftsModal'
import {countriesMap, getTooltipVerification, skipFalsy} from 'shared/lib'
import {UserStatus} from 'shared/types'
import {Button} from 'shared/ui/buttons'
import {Hot, Verified} from 'shared/ui/icons'
import {Tooltip} from 'shared/ui/tooltip'
import {AvatarBox} from 'widgets/avatarBox'
import {useReactivate} from '../../hooks'
import complimentJson from './img/compliment.json'
import styles from './styles.module.scss'

export const ProfileSummary: FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {id} = useParams()
  const isClient = !!id
  const profile = useProfile()
  const user = useUser()
  const is500p = useMediaQuery(`(max-width: ${styles.is500p})`)
  const {
    avatarUrl,
    childrenCount,
    city,
    country,
    name,
    occupation,
    videoAvatarUrl,
    dateBirth,
    education,
    isConfirmed
  } = isClient ? profile : user.profile
  const age = isClient ? profile.age : moment().diff(user.profile.dateBirth, 'years')
  const isOnline = isClient ? profile.isOnline : true
  const {isLoading, reactivateHandler} = useReactivate()

  const openVirtualGiftsModal = useCallback(() => dispatch(virtualGiftsOpenModal()), [dispatch, virtualGiftsOpenModal])

  const editProfileHandler = () => navigate('/settings')

  const isActive = useMemo(() => user.status === UserStatus.ACTIVE, [user.status])

  const navigateChat = () => navigate(`/messages/${id}`)

  const Btn = useCallback(() => {
    if (!isClient && !isActive) {
      return (
        <Button onClick={reactivateHandler} styleType='primary' isDisabled={isLoading} isLoading={isLoading}>
          Reactivate account
        </Button>
      )
    }

    if (!isClient) {
      return (
        <>
          <Button
            styleType='primary'
            className={styles.getPremium}
            onClick={()=> upgradeHandler({dispatch, paymentsCount: user.paymentsCount})}
          >
            {user.paymentsCount > 0 ? 'Refill credits' : 'Upgrade now'}
          </Button>
          <div className={styles.editButton} onClick={editProfileHandler}>
            Edit profile
          </div>
        </>
      )
    }

    return (
      <>
        <div className={`${styles.complimentButton} ${styles.editButton}`} onClick={openVirtualGiftsModal}>
          <Lottie animationData={complimentJson} className={styles.complimentButtonIcon} />
          <div className={styles.text}>Start with a compliment</div>
        </div>
        <VirtualGiftsModal idUser={id} onSendHandler={navigateChat} />
      </>
    )
  }, [user.paymentsCount, user.status, isClient])

  return (
    <div className={styles.profileSummary}>
      {!is500p ? <div className={styles.headerWrapper}>
        <AvatarBox
          canEdit={!isClient}
          src={videoAvatarUrl || avatarUrl || ''}
          type={videoAvatarUrl ? PhotoTypes.VIDEO_AVATAR : avatarUrl ? PhotoTypes.avatar : null}
          alt={name}
          isOnline={isOnline}
          className={styles.avatarBox}
        />
        <div className={styles.headerWrapperContent}>
          <div className={styles.summary}>
            <div className={styles.header}>
              <div className={styles.name}>{skipFalsy(name, String(age || '')).join(', ')}</div>
              <Tooltip title={
                <div>
                  <div className={styles.tooltipHeader}>{isConfirmed ? 'Confirmed' : 'Unconfirmed'} Account</div>
                  <div className={styles.tooltipText}>{getTooltipVerification(isClient, isConfirmed)}</div>
                </div>
              }>
                <div>
                  <div className={`${styles.verifiedIcon} ${isConfirmed ? styles.active : ''}`}>
                    <Verified />
                  </div>
                </div>
              </Tooltip>
              {profile.isPopular ? <Tooltip title={<div className={styles.tooltipHeader}>Popular member</div>}>
                <div className={styles.verifiedIcon}><Hot /></div>
              </Tooltip> : null}
            </div>
            <div className={styles.location}>{city ? city + ',' : ''} {country ? countriesMap[country] : ''}</div>
          </div>

          <div className={styles.buttons}>
            <Btn />
          </div>
        </div>
      </div> : null}
      {isClient
        ? <>
          <div className={`${styles.complimentButton} ${styles.editButton}`} onClick={openVirtualGiftsModal}>
            <Lottie animationData={complimentJson} className={styles.complimentButtonIcon} />
            <div className={styles.text}>Start with a compliment</div>
          </div>
          <VirtualGiftsModal idUser={id} onSendHandler={navigateChat} />
        </>
        : isActive && <Button
          styleType='secondary'
          className={styles.editButton}
          onClick={editProfileHandler}
        >
          Edit profile
        </Button>
      }
      {!isActive ?
        <div className={styles.deactivated}>
          Your profile is deactivated! Reactivate it to use site
        </div>
        : null}
      <div className={styles.info}>
        <div className={styles.field}>
          <span className={styles.label}>Birthday:</span>
          <span className={styles.value}>{moment(dateBirth).format('MMMM DD, YYYY')}</span>
        </div>

        <div className={styles.field}>
          <span className={styles.label}>Children:</span>
          <span className={styles.value}>{childrenCount ? CountChildrenObj[childrenCount] : CountChildrenObj[0]}</span>
        </div>

        <div className={styles.field}>
          <span className={styles.label}>Education:</span>
          <span className={styles.value}>{education ? EducationMap[education] : 'Not specified'}</span>
        </div>

        {occupation 
          ? <div className={`${styles.field} ${styles.occupation}`}>
            <span className={styles.label}>Occupation:</span>
            <span className={styles.value}>{occupation}</span>
          </div>
          : null}
      </div>
    </div>
  )
}